import { TypographyCaption } from '@heidi/design-system/typography';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import type { RunTransport } from '../../../../contracts';
import { useRunStreamLifecycle } from '../../hooks';
import { AgentTimeline, type AgentTimelineProps } from '../agent-timeline';
import { ChildRunInlinePanel, type ChildRunStatus } from './child-run-inline-panel';
import { streamingChildRunPanelMessages } from './streaming-child-run-panel.messages';

export interface StreamingChildRunPanelProps {
  childAgentRunId?: string;
  childAgentName?: string;
  /** Derived from the parent tool row, not the child stream. */
  status?: ChildRunStatus;
  renderItem?: AgentTimelineProps['renderItem'];
  transport?: RunTransport;
  href?: string;
  shouldMountLifecycle?: boolean;
  /**
   * BE cancelTurn on the CHILD run id — the parent turn stays alive to react.
   * May return a promise; a rejection clears the "Stopping" latch for retry.
   */
  onStop?: () => void | Promise<void>;
}

export const StreamingChildRunPanel = ({
  childAgentRunId,
  childAgentName,
  status = 'running',
  renderItem,
  transport,
  href,
  shouldMountLifecycle = true,
  onStop,
}: StreamingChildRunPanelProps) => {
  const canMountLifecycle = !!childAgentRunId && shouldMountLifecycle;
  const active = status === 'running';
  // Collapse once the run settles; the first manual toggle overrides the default.
  const [override, setOverride] = useState<boolean | null>(null);
  const open = canMountLifecycle && (override ?? active);
  const toggle = () => setOverride(!open);

  // Held here so collapsed and expanded panels share one "Stopping" state.
  // Clears only on cancel failure — on success the row flips to cancelled
  // and the button unmounts.
  const [stopRequested, setStopRequested] = useState(false);
  // A panel reused for a different child run must not inherit its latch.
  useEffect(() => {
    setStopRequested(false);
  }, [childAgentRunId]);
  const stopping = stopRequested && active;
  const handleStop =
    onStop && childAgentRunId && active
      ? () => {
          setStopRequested(true);
          // A sync throw must clear the latch like a rejection, or the row
          // wedges on Stopping forever.
          try {
            void Promise.resolve(onStop()).catch(() => setStopRequested(false));
          } catch {
            setStopRequested(false);
          }
        }
      : undefined;

  if (open && childAgentRunId) {
    return (
      <ExpandedChildRunLifecycle
        childAgentRunId={childAgentRunId}
        childAgentName={childAgentName}
        status={status}
        renderItem={renderItem}
        transport={transport}
        href={href}
        onToggle={toggle}
        onStop={handleStop}
        stopping={stopping}
      />
    );
  }

  return (
    <ChildRunInlinePanel
      childAgentName={childAgentName}
      status={status}
      href={href}
      expanded={false}
      canExpand={canMountLifecycle}
      onToggle={toggle}
      onStop={handleStop}
      stopping={stopping}
      awaitingChildRunId={!childAgentRunId}
    />
  );
};

interface ExpandedChildRunLifecycleProps {
  childAgentRunId: string;
  childAgentName?: string;
  status: ChildRunStatus;
  renderItem?: AgentTimelineProps['renderItem'];
  transport?: RunTransport;
  href?: string;
  onToggle: () => void;
  onStop?: () => void;
  stopping?: boolean;
}

const ExpandedChildRunLifecycle = ({
  childAgentRunId,
  childAgentName,
  status,
  renderItem,
  transport,
  href,
  onToggle,
  onStop,
  stopping,
}: ExpandedChildRunLifecycleProps) => {
  const intl = useIntl();
  const { run, items, isLoading, isError } = useRunStreamLifecycle(
    childAgentRunId,
    transport
  );
  const active = status === 'running';
  const approvalRequired = run?.input_state === 'approval_required';

  return (
    <ChildRunInlinePanel
      childAgentName={childAgentName}
      status={status}
      href={href}
      expanded
      canExpand
      onToggle={onToggle}
      onStop={onStop}
      stopping={stopping}
      // Hold the animated skeleton while the child is working but hasn't
      // streamed anything yet, so the open panel never flashes empty.
      isLoading={isLoading || (active && !isError && items.length === 0)}
      unavailable={isError}
    >
      <div className="flex max-h-96 flex-col gap-3 overflow-auto pe-1">
        {approvalRequired && <ChildApprovalRequiredNotice intl={intl} />}
        {items.length === 0 ? (
          <TypographyCaption className="block text-text-secondary">
            {intl.formatMessage(streamingChildRunPanelMessages.noOutput)}
          </TypographyCaption>
        ) : (
          <AgentTimeline items={items} renderItem={renderItem} />
        )}
      </div>
    </ChildRunInlinePanel>
  );
};

const ChildApprovalRequiredNotice = ({ intl }: { intl: IntlShape }) => (
  <div className="flex items-start gap-2 rounded-lg border border-validation-warning-border bg-validation-warning-muted/40 px-2.5 py-2">
    <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-validation-warning" />
    <TypographyCaption className="text-text-secondary">
      {intl.formatMessage(streamingChildRunPanelMessages.approvalWaiting)}
    </TypographyCaption>
  </div>
);
